import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Input, Button } from 'reactstrap';
import moment from 'moment';
import FeatherIconCom from '../../Utils/CommonComponents/CommonIcons/FeatherIconCom';
import {
  CustomTabItem,
  getProfileValues,
  replacePlaceholders,
  normalizeUrl,
} from '../../sharedFunctions/customTabs';

const currentYear = new Date().getFullYear();
const years = [
  { label: String(currentYear), value: currentYear },
  { label: String(currentYear - 1), value: currentYear - 1 },
  { label: String(currentYear - 2), value: currentYear - 2 },
  { label: String(currentYear - 3), value: currentYear - 3 },
];

const months = [
  { label: 'Jan', value: 1 },
  { label: 'Feb', value: 2 },
  { label: 'Mar', value: 3 },
  { label: 'Apr', value: 4 },
  { label: 'May', value: 5 },
  { label: 'Jun', value: 6 },
  { label: 'Jul', value: 7 },
  { label: 'Aug', value: 8 },
  { label: 'Sep', value: 9 },
  { label: 'Oct', value: 10 },
  { label: 'Nov', value: 11 },
  { label: 'Dec', value: 12 },
];

interface CustomTabWebViewProps {
  activeTab: CustomTabItem;
}

export default function CustomTabWebView({ activeTab }: CustomTabWebViewProps) {
  const [loading, setLoading] = useState(true);
  const [finalUrl, setFinalUrl] = useState<string>(activeTab.url);
  const [isDynamic, setIsDynamic] = useState<boolean>(false);

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);

  const updateDynamicUrl = useCallback(
    async (monthVal: number, yearVal: number, actionVal = '') => {
      setLoading(true);
      try {
        const momentDate = moment().year(yearVal).month(monthVal - 1);

        const customParams = {
          month: momentDate.format('M'),
          year: momentDate.format('YYYY'),
          moye: momentDate.format('MMM YYYY'),
          lastmonth: momentDate.clone().subtract(1, 'months').format('M'),
          lastmoye: momentDate.clone().subtract(1, 'months').format('MMM YYYY'),
          action: actionVal,
        };

        let modifiedRawUrl = activeTab.rawUrl || '';

        // Upgrade static urls to dynamic
        if (modifiedRawUrl.match(/month=\d+/i)) {
          modifiedRawUrl = modifiedRawUrl.replace(/month=\d+/gi, 'month={month}');
        }
        if (modifiedRawUrl.match(/year=\d+/i)) {
          modifiedRawUrl = modifiedRawUrl.replace(/year=\d+/gi, 'year={year}');
        }
        if (modifiedRawUrl.includes('action=') && !modifiedRawUrl.includes('{action}')) {
          modifiedRawUrl = modifiedRawUrl.replace(/action=[^&]*/gi, 'action={action}');
        }

        const profileValues = await getProfileValues(customParams);
        const replacedUrl = replacePlaceholders(modifiedRawUrl, profileValues);
        const normalized = normalizeUrl(replacedUrl);

        if (actionVal === 'download') {
          window.open(normalized, '_blank');
          setLoading(false);
          return;
        }

        setFinalUrl(normalized);
      } catch (e) {
        console.error('Error updating custom tab URL:', e);
      } finally {
        setLoading(false);
      }
    },
    [activeTab]
  );

  useEffect(() => {
    const rawUrl = activeTab.rawUrl || '';
    const hasDynamicPlaceholders =
      rawUrl.includes('{month}') ||
      rawUrl.includes('{year}') ||
      rawUrl.includes('{moye}') ||
      rawUrl.includes('{lastmonth}') ||
      rawUrl.includes('{lastmoye}');

    const hasStaticDates = Boolean(rawUrl.match(/month=\d+/i) || rawUrl.match(/year=\d+/i));

    if (hasDynamicPlaceholders || hasStaticDates) {
      setIsDynamic(true);
      updateDynamicUrl(selectedMonth, selectedYear, '');
    } else {
      setIsDynamic(false);
      if (rawUrl && rawUrl.includes('{') && rawUrl.includes('}')) {
        getProfileValues()
          .then((values) => {
            if (values.serverurl) values.serverurl = String(values.serverurl).replace(/\/+$/, '');
            if (values.baseurl) values.baseurl = String(values.baseurl).replace(/\/+$/, '');
            const replaced = replacePlaceholders(rawUrl, values);
            setFinalUrl(normalizeUrl(replaced));
            setLoading(false);
          })
          .catch(() => {
            setFinalUrl(activeTab.url);
            setLoading(false);
          });
      } else {
        setFinalUrl(activeTab.url);
        setLoading(false);
      }
    }
  }, [activeTab, selectedMonth, selectedYear, updateDynamicUrl]);

  useEffect(() => {
    if (isDynamic) {
      updateDynamicUrl(selectedMonth, selectedYear, '');
    }
  }, [selectedMonth, selectedYear, isDynamic, updateDynamicUrl]);

  const rawUrl = activeTab.rawUrl || '';
  const canDownload =
    rawUrl.includes('{action}') ||
    rawUrl.includes('action=') ||
    rawUrl.toLowerCase().includes('getsalaryslip');

  return (
    <div className="custom-tab-container w-100 position-relative py-2">
      {/* Top action bar: filters & external link */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2 px-1">
        {isDynamic ? (
          <div className="d-flex align-items-center gap-2">
            <div style={{ minWidth: '100px' }}>
              <label className="text-muted small mb-1 fw-bold">Month</label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="form-select-sm"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Input>
            </div>

            <div style={{ minWidth: '100px' }}>
              <label className="text-muted small mb-1 fw-bold">Year</label>
              <Input
                type="select"
                bsSize="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="form-select-sm"
              >
                {years.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </Input>
            </div>

            {canDownload && (
              <div className="pt-3">
                <Button
                  color="success"
                  size="sm"
                  className="d-flex align-items-center gap-1"
                  onClick={() => updateDynamicUrl(selectedMonth, selectedYear, 'download')}
                >
                  <FeatherIconCom iconName="Download" size={14} />
                  <span>Download</span>
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-light text-primary border px-2 py-1">
              <i className="icofont icofont-globe me-1" />
              {activeTab.name}
            </span>
          </div>
        )}

        {finalUrl && (
          <div className="ms-auto">
            <a
              href={finalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
              title="Open in new window"
            >
              <FeatherIconCom iconName="ExternalLink" size={14} />
              <span className="d-none d-sm-inline">Open in New Tab</span>
            </a>
          </div>
        )}
      </div>

      {/* Frame Loading State */}
      {loading && (
        <div
          className="d-flex justify-content-center align-items-center w-100"
          style={{ minHeight: '400px' }}
        >
          <div className="text-center">
            <Spinner color="primary" />
            <p className="mt-2 text-muted small">Loading {activeTab.name}...</p>
          </div>
        </div>
      )}

      {/* Embedded Iframe */}
      {finalUrl ? (
        <div
          className="w-100 shadow-sm"
          style={{
            height: isDynamic ? 'calc(100vh - 280px)' : 'calc(100vh - 230px)',
            minHeight: '650px',
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #e9ecef',
            backgroundColor: '#ffffff',
            display: loading ? 'none' : 'block',
          }}
        >
          <iframe
            key={finalUrl}
            src={finalUrl}
            title={activeTab.name || 'Custom Tab Content'}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            onLoad={() => setLoading(false)}
            allow="fullscreen; clipboard-read; clipboard-write; geolocation"
          />
        </div>
      ) : (
        <div className="p-4 text-center text-muted border rounded">
          No URL specified for {activeTab.name}
        </div>
      )}
    </div>
  );
}
